import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { ENDPOINTS } from '../../utilities/endpoints'

export interface BidItem {
  bidId:          string
  propertyId:     string
  buildingId:     string
  currentOwnerId: string
  bidderId:       string
  bidderDetails:  { fullName: string }
  propertyDetails:{ name: string }
  bidAmount:      number
  status:         string
  paymentStatus:  string
  insertDate:     number
}

interface FetchBidsResult {
  totalCount: number
  bidList:    BidItem[]
}

interface FetchBidsParams {
  type:   'owner' | 'bidder'
  limit?:  number
  offset?: number
}

export const fetchBids = createAsyncThunk<
  FetchBidsResult,
  FetchBidsParams,
  { rejectValue: string }
>(
  'bids/fetch',
  async ({ type, limit = 20, offset = 0 }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) throw new Error('No auth token found')

      const params = new URLSearchParams({ type, limit: String(limit), offset: String(offset) })

      const response = await fetch(`${ENDPOINTS.PROPERTIES.BID_LIST}?${params}`, {
        method: 'GET',
        headers: { 'Accept': 'application/json', 'Authorization': token }
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)

      const body: { apiId: string; statusCode: number; message: string; data: FetchBidsResult } = await response.json()
      return body.data
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to fetch bids')
    }
  }
)

interface SubmitBidParams { propertyId: string; bidAmount: number; paymentMethodId: string; isApplePay: boolean; paymentIntentId: string }
export const submitBid = createAsyncThunk<BidItem, SubmitBidParams, { rejectValue: string }>(
  'bids/submit',
  async (payload, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) throw new Error('No auth token found')
      const response = await fetch(`${ENDPOINTS.PROPERTIES.SUBMIT_BID}`, {
        method: 'POST', headers: { 'Accept': 'application/json','Content-Type':'application/json','Authorization':token }, body: JSON.stringify(payload)
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body?.data?.message || body.message || response.statusText)
      return body.data.bid as BidItem
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to submit bid')
    }
  }
)

interface AcceptBidParams { bidId: string }
export const acceptBid = createAsyncThunk<BidItem, AcceptBidParams, { rejectValue: string }>(
  'bids/accept',
  async ({ bidId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) throw new Error('No auth token found')
      const response = await fetch(`${ENDPOINTS.PROPERTIES.ACCEPT_BID}`, {
        method: 'POST', headers: { 'Accept':'application/json','Content-Type':'application/json','Authorization':token }, body: JSON.stringify({ bidId })
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body?.data?.message || body.message || response.statusText)
      return body.data.bid as BidItem
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to accept bid')
    }
  }
)

interface RejectBidParams { bidId: string }
export const rejectBid = createAsyncThunk<BidItem, RejectBidParams, { rejectValue: string }>(
  'bids/reject',
  async ({ bidId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) throw new Error('No auth token found')
      const response = await fetch(`${ENDPOINTS.PROPERTIES.REJECT_BID}`, {
        method: 'POST', headers: { 'Accept':'application/json','Content-Type':'application/json','Authorization':token }, body: JSON.stringify({ bidId })
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body?.data?.message || body.message || response.statusText)
      return body.data.bid as BidItem
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to reject bid')
    }
  }
)

interface CancelBidParams { bidId: string }
export const cancelBid = createAsyncThunk<BidItem, CancelBidParams, { rejectValue: string }>(
  'bids/cancel',
  async ({ bidId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token')
      if (!token) throw new Error('No auth token found')
      const response = await fetch(`${ENDPOINTS.PROPERTIES.CANCEL_BID}`, {
        method: 'POST', headers: { 'Accept':'application/json','Content-Type':'application/json','Authorization':token }, body: JSON.stringify({ bidId })
      })
      const body = await response.json()
      if (!response.ok) throw new Error(body?.data?.message || body.message || response.statusText)
      return body.data.bid as BidItem
    } catch (err: any) {
      return rejectWithValue(err.message ?? 'Failed to cancel bid')
    }
  }
)

interface BidState {
  bidList:      BidItem[]
  totalCount:   number
  isLoading:    boolean
  error:        string | null
  isSubmitting: boolean
  submitError:  string | null
  isAccepting:  boolean
  acceptError:  string | null
  isRejecting:  boolean
  rejectError:  string | null
  isCancelling: boolean
  cancelError:  string | null
}

const initialState: BidState = {
  bidList:      [],
  totalCount:   0,
  isLoading:    false,
  error:        null,
  isSubmitting: false,
  submitError:  null,
  isAccepting:  false,
  acceptError:  null,
  isRejecting:  false,
  rejectError:  null,
  isCancelling: false,
  cancelError:  null
}

const bidSlice = createSlice({
  name: 'bids',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBids.pending, state => { state.isLoading = true; state.error = null })
      .addCase(fetchBids.fulfilled, (state, action) => { state.isLoading = false; state.totalCount = action.payload.totalCount; state.bidList = action.payload.bidList })
      .addCase(fetchBids.rejected, (state, action) => { state.isLoading = false; state.error = action.payload ?? action.error.message! })

      .addCase(submitBid.pending, state => { state.isSubmitting = true; state.submitError = null })
      .addCase(submitBid.fulfilled, (state, action) => { state.isSubmitting = false; state.bidList.unshift(action.payload) })
      .addCase(submitBid.rejected, (state, action) => { state.isSubmitting = false; state.submitError = action.payload ?? action.error.message! })

      .addCase(acceptBid.pending, state => { state.isAccepting = true; state.acceptError = null })
      .addCase(acceptBid.fulfilled, (state, action) => { state.isAccepting = false; const accepted = action.payload; state.bidList = state.bidList.map(b => b.bidId === accepted.bidId ? {...b, status: accepted.status} : b) })
      .addCase(acceptBid.rejected, (state, action) => { state.isAccepting = false; state.acceptError = action.payload ?? action.error.message! })

      .addCase(rejectBid.pending, state => { state.isRejecting = true; state.rejectError = null })
      .addCase(rejectBid.fulfilled, (state, action) => { state.isRejecting = false; const rejected = action.payload; state.bidList = state.bidList.map(b => b.bidId === rejected.bidId ? {...b, status: rejected.status} : b) })
      .addCase(rejectBid.rejected, (state, action) => { state.isRejecting = false; state.rejectError = action.payload ?? action.error.message! })

      .addCase(cancelBid.pending, state => { state.isCancelling = true; state.cancelError = null })
      .addCase(cancelBid.fulfilled, (state, action) => { state.isCancelling = false; const cancelled = action.payload; state.bidList = state.bidList.map(b => b.bidId === cancelled.bidId ? {...b, status: cancelled.status} : b) })
      .addCase(cancelBid.rejected, (state, action) => { state.isCancelling = false; state.cancelError = action.payload ?? action.error.message! })
  }
})

export default bidSlice.reducer
